import { useEffect, useRef, useState } from "react";
import { FileText, Download, Loader, Play, Pause, Mic, X, ZoomIn, LayoutTemplate } from "lucide-react";
import api from "../../services/api";
import { formatTimeOnly } from "./chatUtils";
import { StatusIcon } from "./StatusIcon";

const parseMediaContent = (content) => {
  try {
    const parsed = JSON.parse(content);
    if (parsed.mediaId) return parsed;
  } catch {}
  return { mediaId: content };
};

// ── WhatsApp-style voice player ───────────────────────────────────────────────
const VoicePlayer = ({ url, isOutgoing }) => {
  const audioRef        = useRef(null);
  const [playing, setPlaying]   = useState(false);
  const [current, setCurrent]   = useState(0);
  const [duration, setDuration] = useState(0);

  // 30 fake bars that animate like a waveform
  const bars = Array.from({ length: 30 }, (_, i) => {
    const h = [4, 6, 10, 14, 18, 14, 20, 16, 12, 8,
               18, 22, 16, 10, 14, 20, 24, 18, 12, 8,
               16, 20, 14, 10, 6, 12, 18, 14, 8, 4][i];
    return h;
  });

  const progress = duration ? current / duration : 0;
  const filledBars = Math.round(progress * bars.length);

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play(); }
  };

  const handleBarClick = (i, e) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a || !duration) return;
    a.currentTime = (i / bars.length) * duration;
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay   = () => setPlaying(true);
    const onPause  = () => setPlaying(false);
    const onEnded  = () => { setPlaying(false); setCurrent(0); };
    const onTime   = () => setCurrent(a.currentTime);
    const onLoaded = () => setDuration(a.duration);

    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
    };
  }, []);

  const activeColor   = "#25d366";
  const inactiveColor = isOutgoing ? "rgba(0,0,0,0.2)" : "#c8e6c9";
  const btnBg         = isOutgoing ? "bg-black/10 hover:bg-black/15" : "bg-green-100 hover:bg-green-200";
  const btnColor      = isOutgoing ? "text-gray-700" : "text-green-700";
  const timeColor     = isOutgoing ? "text-gray-400" : "text-gray-400";

  return (
    <div className="flex items-center gap-2.5 w-[220px]">
      <audio ref={audioRef} src={url} preload="metadata" />

      {/* Play / Pause button */}
      <button
        onClick={toggle}
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${btnBg} ${btnColor}`}
      >
        {playing
          ? <Pause className="w-4 h-4 fill-current" />
          : <Play  className="w-4 h-4 fill-current ml-0.5" />}
      </button>

      {/* Waveform + time */}
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        {/* Bars */}
        <div className="flex items-end gap-[2px] h-6">
          {bars.map((h, i) => (
            <button
              key={i}
              onClick={(e) => handleBarClick(i, e)}
              style={{
                height: `${h}px`,
                backgroundColor: i < filledBars ? activeColor : inactiveColor,
                width: "3px",
                borderRadius: "2px",
                flexShrink: 0,
                transition: "background-color 0.1s",
              }}
            />
          ))}
        </div>

        {/* Time row */}
        <div className="flex items-center justify-between">
          <span className={`text-[10px] ${timeColor}`}>
            {playing || current > 0 ? fmt(current) : fmt(duration)}
          </span>
          <Mic className={`w-3 h-3 ${timeColor} opacity-60`} />
        </div>
      </div>
    </div>
  );
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ url, type, filename, caption, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-white text-sm font-medium truncate max-w-[60%]">
          {filename || (type === "video" ? "Video" : "Image")}
        </span>
        <div className="flex items-center gap-2">
          <a
            href={url}
            download={filename || (type === "video" ? "video" : "image")}
            className="flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </a>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Media */}
      <div
        className="flex-1 flex items-center justify-center p-4 min-h-0"
        onClick={onClose}
      >
        {type === "image" ? (
          <img
            src={url}
            alt="preview"
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <video
            src={url}
            controls
            autoPlay
            className="max-w-full max-h-full rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>

      {/* Caption */}
      {caption && (
        <div
          className="px-4 py-3 text-white text-sm text-center flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {caption}
        </div>
      )}
    </div>
  );
};

// ── Media loader ──────────────────────────────────────────────────────────────
const MediaContent = ({ mediaId, type, filename, caption, isOutgoing }) => {
  const [url, setUrl]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!mediaId) { setLoading(false); setError(true); return; }
    let objectUrl;
    api
      .get(`/conversations/media/${mediaId}`, { responseType: "blob" })
      .then((r) => { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [mediaId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1 opacity-60">
        <Loader className="w-4 h-4 animate-spin" />
        <span className="text-xs">Loading…</span>
      </div>
    );
  }
  if (error || !url) return <p className="text-xs opacity-60">[{type} unavailable]</p>;

  if (type === "image") {
    return (
      <>
        {lightbox && <Lightbox url={url} type="image" filename={filename} caption={caption} onClose={() => setLightbox(false)} />}
        <div className="relative group cursor-pointer" onClick={() => setLightbox(true)}>
          <img src={url} alt="img" className="max-w-[260px] max-h-[260px] rounded-xl object-cover" />
          <div className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 drop-shadow transition-opacity" />
          </div>
        </div>
        {caption && <p className="text-sm mt-1 whitespace-pre-wrap break-words">{caption}</p>}
      </>
    );
  }
  if (type === "video") {
    return (
      <>
        {lightbox && <Lightbox url={url} type="video" filename={filename} caption={caption} onClose={() => setLightbox(false)} />}
        <div className="relative group cursor-pointer" onClick={() => setLightbox(true)}>
          <video src={url} className="max-w-[260px] max-h-[200px] rounded-xl object-cover" />
          <div className="absolute inset-0 rounded-xl bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow">
              <Play className="w-5 h-5 text-gray-800 fill-current ml-0.5" />
            </div>
          </div>
        </div>
        {caption && <p className="text-sm mt-1 whitespace-pre-wrap break-words">{caption}</p>}
      </>
    );
  }
  if (type === "audio") {
    return <VoicePlayer url={url} isOutgoing={isOutgoing} />;
  }
  // Document
  return (
    <div>
      <a
        href={url}
        download={filename || "file"}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
          isOutgoing
            ? "border-green-300 bg-green-400 hover:bg-green-300 text-white"
            : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
        }`}
      >
        <FileText className="w-4 h-4 flex-shrink-0" />
        <span className="text-xs truncate max-w-[180px]">{filename || "Download file"}</span>
        <Download className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />
      </a>
      {caption && <p className="text-sm mt-1.5 whitespace-pre-wrap break-words">{caption}</p>}
    </div>
  );
};

// ── Bubble ────────────────────────────────────────────────────────────────────
const MessageBubble = ({ msg }) => {
  const isOutgoing = msg.type === "outgoing";
  const normalizedType =
    msg.messageType === "voice"   ? "audio"
    : msg.messageType === "sticker" ? "image"
    : msg.messageType;
  const isMedia = ["image", "video", "audio", "document"].includes(normalizedType);

  const renderContent = () => {
    if (isMedia) {
      const { mediaId, caption, filename } = parseMediaContent(msg.messageContent || "");
      return (
        <MediaContent
          mediaId={mediaId}
          type={normalizedType}
          filename={filename}
          caption={caption}
          isOutgoing={isOutgoing}
        />
      );
    }

    if (msg.messageType === "template") {
      let tpl = null;
      try { tpl = JSON.parse(msg.messageContent); } catch {}
      // Fallback for old plain-text format
      if (!tpl || !tpl.templateName) {
        return (
          <p className="whitespace-pre-wrap break-words leading-relaxed text-xs opacity-75">
            {msg.messageContent}
          </p>
        );
      }
      return (
        <div className={`rounded-lg overflow-hidden border ${isOutgoing ? "border-green-300/60" : "border-gray-200"}`} style={{ minWidth: 200, maxWidth: 260 }}>
          {/* Media header */}
          {tpl.headerUrl && tpl.headerType === "image" && (
            <img
              src={tpl.headerUrl}
              alt="header"
              className="w-full object-cover max-h-36"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          {tpl.headerUrl && tpl.headerType === "video" && (
            <video
              src={tpl.headerUrl}
              className="w-full max-h-36 object-cover bg-black"
              controls={false}
              muted
              preload="metadata"
            />
          )}
          {tpl.headerUrl && tpl.headerType === "document" && (
            <div className={`flex items-center gap-2 px-2.5 py-2 ${isOutgoing ? "bg-black/5" : "bg-gray-50"}`}>
              <FileText className="w-4 h-4 opacity-60 flex-shrink-0" />
              <span className="text-xs opacity-70 truncate">{tpl.headerUrl.split("/").pop()}</span>
            </div>
          )}
          {/* Text header */}
          {tpl.headerType === "text" && tpl.headerUrl && (
            <p className={`text-xs font-semibold px-2.5 pt-2 pb-0`}>{tpl.headerUrl}</p>
          )}

          {/* Label bar */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 ${isOutgoing ? "bg-black/5" : "bg-gray-50"}`}>
            <LayoutTemplate className="w-3 h-3 opacity-50 flex-shrink-0" />
            <span className="text-[9px] font-semibold uppercase tracking-wide opacity-50">Template</span>
            <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-medium ${isOutgoing ? "bg-black/10 text-gray-600" : "bg-gray-200 text-gray-500"}`}>
              {tpl.category}
            </span>
          </div>

          {/* Body */}
          {tpl.body ? (
            <p className="text-sm px-2.5 py-2 whitespace-pre-wrap break-words leading-relaxed">
              {tpl.body}
            </p>
          ) : (
            <p className="text-xs px-2.5 py-2 opacity-50 italic">{tpl.templateName}</p>
          )}

          {/* Footer */}
          {tpl.footerText && (
            <p className="text-[10px] px-2.5 pb-2 opacity-55">{tpl.footerText}</p>
          )}
        </div>
      );
    }

    return (
      <p className="whitespace-pre-wrap break-words leading-relaxed">
        {msg.messageContent || `[${msg.messageType}]`}
      </p>
    );
  };

  return (
    <div className={`flex mb-2 ${isOutgoing ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
          isOutgoing
            ? "bg-[#d9fdd3] text-gray-800 rounded-tr-sm shadow-sm"
            : "bg-white text-gray-800 shadow-sm rounded-tl-sm"
        }`}
      >
        {!isOutgoing && (
          <p className="text-xs font-semibold text-blue-600 mb-1">{msg.userName}</p>
        )}
        {renderContent()}
        <div className={`flex items-center gap-1 mt-1 ${isOutgoing ? "justify-end" : "justify-start"}`}>
          <span className={`text-xs ${isOutgoing ? "text-gray-400" : "text-gray-400"}`}>
            {formatTimeOnly(msg.timestamp)}
          </span>
          {isOutgoing && (
            msg.status === "failed" && msg.error ? (
              <div className="relative group">
                <StatusIcon status="failed" />
                <div className="absolute bottom-full right-0 mb-1.5 w-56 bg-gray-900 text-white text-[11px] rounded-lg px-2.5 py-2 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-20 leading-relaxed">
                  <p className="font-semibold text-red-300 mb-0.5">Failed to deliver</p>
                  <p>{msg.error}</p>
                </div>
              </div>
            ) : (
              <StatusIcon status={msg.status} />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
